import axios from "axios";
import { shouldWipeTokensOn401 } from "@/lib/authBoot";
import { isSiteWalkthroughPath, siteWalkthroughAdapter } from "@/lib/siteWalkthroughApi";

export { apiDetail } from "@/lib/apiErrors";

// Backend URL resolution:
// 1. Use REACT_APP_BACKEND_URL if provided at build time (Emergent preview).
// 2. Otherwise fall back to the current window origin. This lets the app work
//    on custom domains (e.g. https://striklenz.com) without a rebuild,
//    provided the domain proxies `/api/*` to the FastAPI backend.
const _envBackend = (process.env.REACT_APP_BACKEND_URL || "").trim();
const _hasEnvBackend = _envBackend && _envBackend !== "undefined" && _envBackend !== "null";
const BACKEND_URL = _hasEnvBackend
  ? _envBackend
  : (typeof window !== "undefined" ? window.location.origin : "");
export const API = `${BACKEND_URL}/api`;

// NOTE: withCredentials MUST be false. The Emergent preview ingress (Cloudflare)
// overrides `Access-Control-Allow-Origin` to `*`, and browsers reject `*` +
// credentials mode. We authenticate via `X-Admin-Token` / `X-Guest-Token`
// headers (see the request interceptor below) — no cookies are used — so
// disabling withCredentials is safe.
export const api = axios.create({ baseURL: API, timeout: 20000, withCredentials: false });

export const INDEX_ADMIN_TIMEOUT_MS = 90000;

// Simple deduped helper for the /tickers/extras endpoint so multiple callers
// won't create duplicate concurrent network requests. Keeps one inflight promise
// and returns its resolved data to all callers.
let __inflightExtras = null;
let __lastExtrasFetchAt = 0;
let __extrasCache = null; // { data, fetchedAt }
let __walkthroughExtrasInflight = null;
let __walkthroughExtrasCache = null;
const EXTRAS_CACHE_TTL_MS = 3000; // 3s cache to coalesce very rapid repeat callers

// Singleton poller state
let __extrasSubscribers = new Set();
let __extrasPollerId = null;
let __extrasPollMs = 30_000; // default poll interval
let __walkthroughExtrasSubscribers = new Set();
let __walkthroughExtrasPollerId = null;
let __walkthroughExtrasStartTimer = null;

export async function fetchExtras() {
  const now = Date.now();

  if (isSiteWalkthroughPath()) {
    if (__walkthroughExtrasCache && now - __walkthroughExtrasCache.fetchedAt < EXTRAS_CACHE_TTL_MS) {
      return __walkthroughExtrasCache.data;
    }
    if (__walkthroughExtrasInflight) return __walkthroughExtrasInflight;
    __walkthroughExtrasInflight = api.get("/tickers/extras").then((r) => {
      __walkthroughExtrasCache = { data: r.data, fetchedAt: Date.now() };
      return r.data;
    }).finally(() => {
      __walkthroughExtrasInflight = null;
    });
    return __walkthroughExtrasInflight;
  }

  // Return cached data for very short TTL to avoid bursty duplicate requests
  if (__extrasCache && now - (__extrasCache.fetchedAt || 0) < EXTRAS_CACHE_TTL_MS) {
    if (process.env.NODE_ENV !== 'production') {
      try { console.debug('[fetchExtras] returning cached extras', { ageMs: now - __extrasCache.fetchedAt }); } catch (_) {}
    }
    return Promise.resolve(__extrasCache.data);
  }

  // If a fetch is already in-flight, reuse it
  if (__inflightExtras) {
    if (process.env.NODE_ENV !== 'production') {
      try { console.debug('[fetchExtras] returning existing inflight promise', { now: new Date(now).toISOString() }); console.trace('[fetchExtras] callers stack'); } catch (_) {}
    }
    return __inflightExtras;
  }

  if (process.env.NODE_ENV !== 'production') {
    try {
      console.debug('[fetchExtras] creating new network request', { time: new Date(now).toISOString() });
      console.trace('[fetchExtras] stack trace for creator');
    } catch (_) {}
  }

  __inflightExtras = api.get("/tickers/extras").then((r) => {
    const data = r.data;
    try {
      __lastExtrasFetchAt = Date.now();
      __extrasCache = { data, fetchedAt: Date.now() };
    } catch (_) {}
    return data;
  }).catch((e) => {
    // On error, keep previous cache (don't clobber) and rethrow
    if (process.env.NODE_ENV !== 'production') {
      try { console.debug('[fetchExtras] network error, keeping previous cache if any', e?.message || e); } catch (_) {}
    }
    throw e;
  }).finally(() => { __inflightExtras = null; });

  return __inflightExtras;
}

// Internal: notify subscribers with latest data
function __notifyExtrasSubscribers(data, meta = {}) {
  try {
    __extrasSubscribers.forEach((cb) => {
      try { cb(data, meta); } catch (_) {}
    });
  } catch (_) {}
}

// Start the singleton poller if not running
function __startExtrasPoller(ms = __extrasPollMs) {
  if (__extrasPollerId) return;
  __extrasPollMs = ms;
  if (process.env.NODE_ENV !== 'production') try { console.debug('[extrasPoller] starting with ms=', ms); } catch (_) {}
  // run immediately once
  (async () => {
    try {
      const data = await fetchExtras();
      __notifyExtrasSubscribers(data, { source: 'init' });
    } catch (e) { if (process.env.NODE_ENV !== 'production') try { console.debug('[extrasPoller] initial fetch failed', e?.message || e); } catch (_) {} }
  })();
  __extrasPollerId = setInterval(async () => {
    try {
      const data = await fetchExtras();
      __notifyExtrasSubscribers(data, { source: 'poll' });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') try { console.debug('[extrasPoller] poll error', e?.message || e); } catch (_) {}
    }
  }, ms);
}

// Stop the poller
function __stopExtrasPoller() {
  if (!__extrasPollerId) return;
  clearInterval(__extrasPollerId);
  __extrasPollerId = null;
  if (process.env.NODE_ENV !== 'production') try { console.debug('[extrasPoller] stopped'); } catch (_) {}
}

function __startWalkthroughExtrasPoller(ms = 30_000) {
  if (__walkthroughExtrasPollerId) return;
  const load = async (source) => {
    try {
      const data = await fetchExtras();
      __walkthroughExtrasSubscribers.forEach((subscriber) => {
        try { subscriber(data, { source }); } catch (_) {}
      });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[walkthrough extras] sample fetch failed", error?.message || error);
      }
    }
  };
  load("init");
  __walkthroughExtrasPollerId = setInterval(() => load("poll"), ms);
}

function __stopWalkthroughExtrasPoller() {
  if (__walkthroughExtrasPollerId) clearInterval(__walkthroughExtrasPollerId);
  if (__walkthroughExtrasStartTimer) clearTimeout(__walkthroughExtrasStartTimer);
  __walkthroughExtrasPollerId = null;
  __walkthroughExtrasStartTimer = null;
}

// Public: subscribe to extras updates. Returns an unsubscribe function.
export function subscribeExtras(cb, options = {}) {
  const { immediate = true, pollMs = __extrasPollMs, delayMs = 0 } = options || {};
  if (isSiteWalkthroughPath()) {
    __walkthroughExtrasSubscribers.add(cb);
    if (immediate && __walkthroughExtrasCache) {
      try { cb(__walkthroughExtrasCache.data, { source: "walkthrough-cache" }); } catch (_) {}
    }
    if (delayMs > 0 && !__walkthroughExtrasPollerId && !__walkthroughExtrasStartTimer) {
      __walkthroughExtrasStartTimer = setTimeout(() => {
        __walkthroughExtrasStartTimer = null;
        if (__walkthroughExtrasSubscribers.size) __startWalkthroughExtrasPoller(pollMs);
      }, delayMs);
    } else if (delayMs === 0) {
      __startWalkthroughExtrasPoller(pollMs);
    }
    return () => {
      __walkthroughExtrasSubscribers.delete(cb);
      if (__walkthroughExtrasSubscribers.size === 0) __stopWalkthroughExtrasPoller();
    };
  }
  __extrasSubscribers.add(cb);
  if (delayMs > 0 && !__extrasPollerId) {
    setTimeout(() => {
      if (__extrasSubscribers.size) __startExtrasPoller(pollMs);
    }, delayMs);
  } else {
    __startExtrasPoller(pollMs);
  }
  // if we have cached data, immediately notify
  if (immediate && __extrasCache && __extrasCache.data) {
    try { cb(__extrasCache.data, { source: 'cache' }); } catch (_) {}
  }
  return () => { unsubscribeExtras(cb); };
}

export function unsubscribeExtras(cb) {
  __extrasSubscribers.delete(cb);
  if (__extrasSubscribers.size === 0) __stopExtrasPoller();
}

const authStorage = {
  get(key) {
    try {
      // Prefer sessionStorage for ephemeral sessions, but fall back to localStorage
      const s = sessionStorage.getItem(key);
      if (s) return s;
      try {
        return localStorage.getItem(key);
      } catch (_) {
        return null;
      }
    } catch (_) {
      // sessionStorage may be unavailable in some contexts (e.g., third-party frames)
      try { return localStorage.getItem(key); } catch (_) { return null; }
    }
  },
  set(key, value) {
    try { sessionStorage.setItem(key, value); } catch (_) {}
  },
  remove(key) {
    try { sessionStorage.removeItem(key); } catch (_) {}
    try { localStorage.removeItem(key); } catch (_) {}
  },
};

export function clearGuestAuth() {
  authStorage.remove("oi_guest_token");
  authStorage.remove("oi_guest_name");
  authStorage.remove("oi_guest_expires_at");
  try {
    sessionStorage.removeItem("oi_access_request_id");
    sessionStorage.removeItem("oi_access_request_name");
  } catch (_) {}
}

/** Server-side guest Exit: revoke session + IP opt-out so auto-admit does not bounce them back in. */
export async function logoutGuest() {
  try {
    await api.post("/auth/guest/logout");
  } catch (_) {
    /* still clear client — best effort */
  }
  clearGuestAuth();
}

export function persistGuestAuth({ token, name, expiresInSeconds, expiresAt }) {
  if (!token) return;
  try { sessionStorage.setItem("oi_guest_token", token); } catch (_) {}
  try { localStorage.setItem("oi_guest_token", token); } catch (_) {}
  if (name != null) {
    try { sessionStorage.setItem("oi_guest_name", name); } catch (_) {}
    try { localStorage.setItem("oi_guest_name", name); } catch (_) {}
  }
  let expiresMs = null;
  if (expiresAt) {
    const parsed = Date.parse(expiresAt);
    if (!Number.isNaN(parsed)) expiresMs = parsed;
  }
  if (expiresMs == null && expiresInSeconds != null) {
    expiresMs = Date.now() + (Number(expiresInSeconds) * 1000);
  }
  if (expiresMs != null) {
    try { sessionStorage.setItem("oi_guest_expires_at", String(expiresMs)); } catch (_) {}
    try { localStorage.setItem("oi_guest_expires_at", String(expiresMs)); } catch (_) {}
  }
}

export function persistAdminSession(token) {
  if (!token) return;
  try { sessionStorage.setItem("oi_admin_token", token); } catch (_) {}
  // Never leave a stale localStorage admin token — it blocked Remember-me.
  try { localStorage.removeItem("oi_admin_token"); } catch (_) {}
}

export function clearAdminAuth({ clearRemember = false } = {}) {
  authStorage.remove("oi_admin_token");
  if (clearRemember) {
    try { localStorage.removeItem("oi_admin_remember_token"); } catch (_) {}
  }
}

// Attach admin OR guest token (mutually exclusive — admin wins).
api.interceptors.request.use((config) => {
  if (isSiteWalkthroughPath()) return config;
  try {
    const at = authStorage.get("oi_admin_token");
    if (at) {
      config.headers["X-Admin-Token"] = at;
      // Do not also send a leftover guest token — confuses auth/state + last_seen.
      return config;
    }
    const gt = authStorage.get("oi_guest_token");
    if (gt) config.headers["X-Guest-Token"] = gt;

    // Dev-time tracing for extras endpoint to find duplicate callers
    if (process.env.NODE_ENV !== 'production' && config && config.url && String(config.url).includes('/tickers/extras')) {
      try {
        console.debug('[api] outgoing /tickers/extras request; config:', config);
        console.trace('[api] stack trace for /tickers/extras');
      } catch (_) {}
    }
  } catch (_) { /* ignore */ }
  return config;
});

api.interceptors.request.use((config) => {
  if (isSiteWalkthroughPath() && !config.skipSiteWalkthroughMock) {
    config.adapter = siteWalkthroughAdapter;
  }
  return config;
});

// Global 401 handler — clear ephemeral session tokens so AuthGate can re-auth.
// Keep Remember-me token so the next load can restore the admin session.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const url = String(err?.config?.url || "");
      if (!shouldWipeTokensOn401(url)) {
        return Promise.reject(err);
      }
      try {
        clearAdminAuth({ clearRemember: false });
        clearGuestAuth();
      } catch (_) { /* ignore */ }
    }
    return Promise.reject(err);
  },
);

export const fetchStatus = () => api.get("/status", { timeout: 8000 }).then((r) => r.data);
export const fetchOI = (idx) => api.get(`/oi/${idx}`).then((r) => r.data);

let __configInflight = null;
let __configCache = null;
const CONFIG_TTL_MS = 15_000;

export function invalidateConfigCache() {
  __configCache = null;
  __configInflight = null;
}

/** One in-flight /config for Dashboard + BigClock (both used to stampede). */
export function fetchConfig(opts = {}) {
  const force = !!opts.force;
  const now = Date.now();
  if (isSiteWalkthroughPath()) {
    return api
      .get("/config", { timeout: 4000, params: force ? { _: now } : undefined })
      .then((r) => r.data);
  }
  if (!force && __configCache && now - __configCache.at < CONFIG_TTL_MS) {
    return Promise.resolve(__configCache.data);
  }
  if (!force && __configInflight) return __configInflight;
  __configInflight = api
    .get("/config", { timeout: 4000, params: force ? { _: now } : undefined })
    .then((r) => {
      __configCache = { data: r.data, at: Date.now() };
      return r.data;
    })
    .finally(() => {
      __configInflight = null;
    });
  return __configInflight;
}
export const fetchOIChange = (idx, minutes, opts = {}) => {
  const params = { minutes };
  if (opts.expiry) params.expiry = opts.expiry;
  if (opts.also) params.also = Array.isArray(opts.also) ? opts.also.join(",") : opts.also;
  const timeout = opts.timeout;
  return api.get(`/oi/${idx}/change`, timeout ? { params, timeout } : { params }).then((r) => r.data);
};
export const fetchAlerts = () => api.get("/alerts").then((r) => r.data);
export const fetchTickers = () => api.get("/tickers").then((r) => r.data);
export const fetchJournalMonth = (year, month) =>
  api.get("/journal", { params: { year, month } }).then((r) => r.data);
export const syncJournal = () =>
  api.post("/journal/sync").then((r) => r.data);
export const fetchJournalYear = (year) =>
  api.get(`/journal/year/${year}`).then((r) => r.data);
export const fetchJournalPeriod = (from, to, index) =>
  api.get("/journal/period", { params: { from, to, ...(index && index !== "ALL" ? { index } : {}) } }).then((r) => r.data);
export const fetchJournalDay = (day) =>
  api.get(`/journal/${day}`).then((r) => r.data);
export const saveJournalDay = (day, body) =>
  api.put(`/journal/${day}`, body).then((r) => r.data);
export const addJournalScreenshot = (day, body) =>
  api.post(`/journal/${day}/screenshot`, body).then((r) => r.data);
export const deleteJournalScreenshot = (day, id) =>
  api.delete(`/journal/${day}/screenshot/${id}`).then((r) => r.data);
export const fetchMemberJournalMonth = (year, month) =>
  api.get("/membership/journal", { params: { year, month } }).then((r) => r.data);
export const fetchMemberJournalYear = (year) =>
  api.get("/membership/journal", { params: { year, view: "year" } }).then((r) => r.data);
export const fetchMemberJournalPeriod = (from, to) =>
  api.get("/membership/journal-period", { params: { from, to } }).then((r) => r.data);
export const fetchMemberJournalDay = (day) =>
  api.get(`/membership/journal/${day}`).then((r) => r.data);
export const saveMemberJournalDay = (day, body) =>
  api.put(`/membership/journal/${day}`, body).then((r) => r.data);
export const addMemberJournalScreenshot = (day, body) =>
  api.post(`/membership/journal/${day}/screenshot`, body).then((r) => r.data);
export const deleteMemberJournalScreenshot = (day, id) =>
  api.delete(`/membership/journal/${day}/screenshot/${id}`).then((r) => r.data);

function filenameFromDisposition(header, fallback) {
  const raw = String(header || "");
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(raw);
  if (!m) return fallback;
  try {
    return decodeURIComponent(m[1].replace(/"/g, "").trim());
  } catch {
    return m[1].replace(/"/g, "").trim() || fallback;
  }
}

export const fetchTrades = (from, to, index) =>
  api.get("/trades", { params: { from, to, ...(index && index !== "ALL" ? { index } : {}) } }).then((r) => r.data);

export async function downloadTradesExcel({ from, to, index } = {}) {
  const params = { from, to };
  if (index && index !== "ALL") params.index = index;
  const r = await api.get("/trades/export", {
    params,
    responseType: "blob",
    timeout: 45000,
  });
  const type = r.headers["content-type"] || "";
  if (type.includes("application/json")) {
    const text = await r.data.text();
    let detail = "Could not download trades";
    try {
      detail = JSON.parse(text)?.detail || detail;
    } catch { /* blob was not json */ }
    throw new Error(detail);
  }
  const blob = r.data instanceof Blob ? r.data : new Blob([r.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const name = filenameFromDisposition(
    r.headers["content-disposition"],
    `striklenz-trades-${from}-to-${to}.xlsx`,
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return name;
}

export const fetchTradeCycleArchiveRange = (fromDate, toDate) =>
  api.get("/trades/archive/range", { params: { from: fromDate, to: toDate } }).then((r) => r.data);

export async function downloadTradeCycleArchive(fromDate, toDate) {
  const r = await api.get("/trades/archive/export", {
    params: { from: fromDate, to: toDate },
    responseType: "blob",
    timeout: 120000,
  });
  const type = r.headers["content-type"] || "";
  if (type.includes("application/json")) {
    const text = await r.data.text();
    let detail = "Could not download trade archive";
    try {
      detail = JSON.parse(text)?.detail || detail;
    } catch { /* blob was not json */ }
    throw new Error(detail);
  }
  const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/gzip" });
  const name = filenameFromDisposition(
    r.headers["content-disposition"],
    `striklenz-cycle-archive-${fromDate}-to-${toDate}.jsonl.gz`,
  );
  const sha256 = r.headers["x-archive-sha256"];
  const count = Number(r.headers["x-archive-cycle-count"] || 0);
  if (!sha256 || !count) throw new Error("Archive response is missing its verification details");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return { name, sha256, count };
}

export const compactTradeCycleArchive = (fromDate, toDate, sha256) =>
  api.post(
    "/trades/archive/compact",
    { from: fromDate, to: toDate, sha256 },
    { timeout: 120000 },
  ).then((r) => r.data);

export const deleteTradeCycleArchive = (fromDate, toDate, sha256) =>
  api.post(
    "/trades/archive/delete",
    { from: fromDate, to: toDate, sha256 },
    { timeout: 120000 },
  ).then((r) => r.data);

export const fetchVRP = (idx, days = 30) =>
  api.get(`/vrp/${idx}`, { params: { days } }).then((r) => r.data);
export const fetchStraddle = (idx, opts = {}) =>
  api.get(`/straddle/${idx}`, { params: opts }).then((r) => r.data);
export const fetchStraddleTick = (idx, opts = {}) =>
  api.get(`/straddle/${idx}/tick`, { params: opts }).then((r) => r.data);

const __straddleHist = new Map();
const STRADDLE_HIST_TTL_MS = 45_000;

export const fetchStraddleHistory = (idx, minutes = 60, opts = {}) => {
  const params = { ...opts };
  if (minutes != null) params.minutes = minutes;
  const key = `${idx}|${params.minutes || ""}|${params.expiry || ""}|${params.date || ""}`;
  if (isSiteWalkthroughPath()) {
    return api.get(`/straddle/${idx}/history`, { params, timeout: 20000 }).then((r) => r.data);
  }
  const hit = __straddleHist.get(key);
  if (hit?.data && Date.now() - hit.at < STRADDLE_HIST_TTL_MS) return Promise.resolve(hit.data);
  if (hit?.inflight) return hit.inflight;
  const inflight = api.get(`/straddle/${idx}/history`, { params, timeout: 20000 }).then((r) => {
    __straddleHist.set(key, { at: Date.now(), data: r.data });
    return r.data;
  }).catch((e) => {
    __straddleHist.delete(key);
    throw e;
  });
  __straddleHist.set(key, { inflight, at: 0 });
  return inflight;
};

export function prefetchStraddleHistory(idx, opts = {}) {
  if (!idx) return Promise.resolve();
  return fetchStraddleHistory(idx, 60, opts).catch(() => {});
}
export const clearAlerts = () => api.delete("/alerts").then((r) => r.data);
export const saveCredentials = (api_key, access_token) =>
  api.post("/credentials", { api_key, access_token }).then((r) => r.data);
export const credentialsStatus = () =>
  api.get("/credentials/status").then((r) => r.data);
export const kiteVaultStatus = () =>
  api.get("/kite/vault").then((r) => r.data);
export const saveKiteVault = (payload) =>
  api.post("/kite/vault", payload).then((r) => r.data);
export const clearKiteVault = () =>
  api.delete("/kite/vault").then((r) => r.data);
export const refreshKiteSession = (request_token) =>
  api.post("/kite/refresh", { request_token }).then((r) => r.data);
export const userKiteStatus = () =>
  api.get("/kite/user/status").then((r) => r.data);
export const saveUserKiteCredentials = (api_key, api_secret) =>
  api.put("/kite/user/credentials", { api_key, api_secret }).then((r) => r.data);
export const deleteUserKiteCredentials = () =>
  api.delete("/membership/broker-credentials").then((r) => r.data);
export const memberBrokerCredentialsStatus = () =>
  api.get("/membership/broker-credentials").then((r) => r.data);
export const userKiteLoginUrl = () =>
  api.get("/kite/user/login-url").then((r) => r.data);
export const completeUserKiteSession = (request_token) =>
  api.post("/kite/user/session", { request_token }).then((r) => r.data);
export const disconnectUserKite = () =>
  api.post("/kite/user/disconnect").then((r) => r.data);
export const setMode = (mode) => api.post("/mode", { mode }).then((r) => r.data);

if (typeof window !== "undefined") {
  window.addEventListener("oi-settings-saved", () => {
    invalidateConfigCache();
  });
}
